/**
 * Phase 14 Plan 14-02 — EntryHistoryTimeline
 *
 * Vertical timeline showing the prior-transition history of a logsheet entry
 * (e.g. Submitted → Checked → Rejected → Submitted → Checked).
 *
 * Each step shows: actor, action, timestamp, optional reason + remarks.
 */

import type { JSX } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { EntryHistoryItem } from '@/types/logsheet-approver';

export interface EntryHistoryTimelineProps {
  readonly history: readonly EntryHistoryItem[];
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function EntryHistoryTimeline({ history }: EntryHistoryTimelineProps): JSX.Element {
  return (
    <Card data-testid="entry-history-timeline">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Entry History</CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="entry-history-timeline-empty">
            No history yet.
          </p>
        ) : (
          <ol className="space-y-4">
            {history.map((step, index) => (
              <li
                key={`${step.actor}-${step.at}-${index}`}
                className="relative flex gap-3"
                data-testid={`entry-history-timeline-step-${String(index)}`}
              >
                {/* Connector line */}
                {index < history.length - 1 && (
                  <div className="absolute left-3 top-6 h-full w-px bg-border" aria-hidden />
                )}
                {/* Dot */}
                <div className="mt-1 h-6 w-6 shrink-0 rounded-full border-2 border-primary bg-background" />
                {/* Content */}
                <div className="flex-1 space-y-1 pb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="text-sm font-medium"
                      data-testid={`entry-history-timeline-step-${String(index)}-action`}
                    >
                      {step.action}
                    </span>
                    <span className="text-xs text-muted-foreground">by {step.actor}</span>
                  </div>
                  <div
                    className="font-mono text-xs text-muted-foreground"
                    data-testid={`entry-history-timeline-step-${String(index)}-at`}
                  >
                    {formatDateTime(step.at)}
                  </div>
                  {step.reason !== null && (
                    <div className="text-xs">
                      <span className="text-muted-foreground">Reason:</span>{' '}
                      <span>{step.reason}</span>
                    </div>
                  )}
                  {step.remarks !== null && (
                    <div className="text-xs text-muted-foreground">{step.remarks}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
